/**
 * Theme presets — curated starting points. Each is a full theme token blob.
 * Selecting a preset replaces the entire palette + typography + shape/density.
 */
export const THEME_PRESETS = [
  {
    id: "medical-teal",
    name: "Medical Teal",
    description: "Clean, light, clinical — soft white surfaces with a deep teal accent.",
    swatch: ["#FFFFFF", "#0D9488", "#F0FDFA"],
    tokens: {
      "--bg": "#FFFFFF", "--surface": "#FFFFFF", "--surface-2": "#F8FAFC",
      "--border": "#E2E8F0", "--muted": "#94A3B8",
      "--text": "#0F172A", "--text-muted": "#475569",
      "--brand": "#0D9488", "--brand-fg": "#FFFFFF",
      "--accent": "#0D9488", "--success": "#10B981", "--danger": "#EF4444",
      "--font-display": '"Inter", system-ui, sans-serif',
      "--font-body": '"Inter", system-ui, sans-serif',
      "--font-mono": '"Inter", system-ui, sans-serif',
      "--font-scale": "1",
      "--radius-sm": "6px", "--radius-md": "8px", "--radius-lg": "12px",
      "--density-y": "1", "--density-x": "1",
    },
  },
  {
    id: "gold-black",
    name: "Gold on Black",
    description: "The default Amino Exchange terminal aesthetic.",
    swatch: ["#09090B", "#F0B90B", "#2563EB"],
    tokens: {
      "--bg": "#09090B", "--surface": "#0e0e11", "--surface-2": "#18181b",
      "--border": "#27272a", "--muted": "#71717a",
      "--text": "#f4f4f5", "--text-muted": "#a1a1aa",
      "--brand": "#F0B90B", "--brand-fg": "#000000",
      "--accent": "#2563EB", "--success": "#22C55E", "--danger": "#EF4444",
      "--font-display": '"Barlow Condensed", Impact, sans-serif',
      "--font-body": '"Manrope", Inter, system-ui, sans-serif',
      "--font-mono": '"JetBrains Mono", Menlo, ui-monospace, monospace',
      "--font-scale": "1",
      "--radius-sm": "0px", "--radius-md": "0px", "--radius-lg": "0px",
      "--density-y": "1", "--density-x": "1",
    },
  },
  {
    id: "emerald-terminal",
    name: "Emerald Terminal",
    description: "Matrix-y forest green on charcoal.",
    swatch: ["#050a08", "#10B981", "#A7F3D0"],
    tokens: {
      "--bg": "#050a08", "--surface": "#0a1410", "--surface-2": "#0f1a15",
      "--border": "#1f3a2f", "--muted": "#5c8b7a",
      "--text": "#e6fff5", "--text-muted": "#8fbfa9",
      "--brand": "#10B981", "--brand-fg": "#00120a",
      "--accent": "#A7F3D0", "--success": "#22C55E", "--danger": "#EF4444",
      "--font-display": '"Space Grotesk", sans-serif',
      "--font-body": '"IBM Plex Sans", system-ui, sans-serif',
      "--font-mono": '"JetBrains Mono", monospace',
      "--font-scale": "1",
      "--radius-sm": "2px", "--radius-md": "4px", "--radius-lg": "6px",
      "--density-y": "1", "--density-x": "1",
    },
  },
  {
    id: "blood-orange",
    name: "Blood Orange",
    description: "High-energy orange on true black.",
    swatch: ["#0a0000", "#FF5A1F", "#FDBA74"],
    tokens: {
      "--bg": "#0a0000", "--surface": "#140505", "--surface-2": "#1a0808",
      "--border": "#3a1810", "--muted": "#a06455",
      "--text": "#fff5ee", "--text-muted": "#d4a898",
      "--brand": "#FF5A1F", "--brand-fg": "#150300",
      "--accent": "#FDBA74", "--success": "#22C55E", "--danger": "#DC2626",
      "--font-display": '"Bebas Neue", Impact, sans-serif',
      "--font-body": '"Inter", system-ui, sans-serif',
      "--font-mono": '"IBM Plex Mono", monospace',
      "--font-scale": "1",
      "--radius-sm": "0px", "--radius-md": "0px", "--radius-lg": "0px",
      "--density-y": "1", "--density-x": "1",
    },
  },
  {
    id: "minimal-light",
    name: "Minimal Light",
    description: "Clean white with a single accent — Notion-adjacent.",
    swatch: ["#fafafa", "#111827", "#3B82F6"],
    tokens: {
      "--bg": "#fafafa", "--surface": "#ffffff", "--surface-2": "#f4f4f5",
      "--border": "#e4e4e7", "--muted": "#a1a1aa",
      "--text": "#111827", "--text-muted": "#52525b",
      "--brand": "#111827", "--brand-fg": "#ffffff",
      "--accent": "#3B82F6", "--success": "#059669", "--danger": "#DC2626",
      "--font-display": '"Inter", system-ui, sans-serif',
      "--font-body": '"Inter", system-ui, sans-serif',
      "--font-mono": '"IBM Plex Mono", monospace',
      "--font-scale": "1",
      "--radius-sm": "6px", "--radius-md": "10px", "--radius-lg": "14px",
      "--density-y": "1.1", "--density-x": "1.1",
    },
  },
  {
    id: "cyberpunk",
    name: "Cyberpunk",
    description: "Magenta + cyan on midnight — for the maximalists.",
    swatch: ["#0a0016", "#F0F", "#0FF"],
    tokens: {
      "--bg": "#0a0016", "--surface": "#140027", "--surface-2": "#1a0033",
      "--border": "#3a0f66", "--muted": "#8b5cf6",
      "--text": "#fce7ff", "--text-muted": "#c4b5fd",
      "--brand": "#FF00FF", "--brand-fg": "#0a0016",
      "--accent": "#00FFFF", "--success": "#00FF88", "--danger": "#FF0055",
      "--font-display": '"Orbitron", "Bebas Neue", sans-serif',
      "--font-body": '"Rajdhani", system-ui, sans-serif',
      "--font-mono": '"Share Tech Mono", monospace',
      "--font-scale": "1",
      "--radius-sm": "0px", "--radius-md": "0px", "--radius-lg": "0px",
      "--density-y": "1", "--density-x": "1",
    },
  },
  {
    id: "nautical",
    name: "Nautical Deep",
    description: "Deep navy with copper accent — apothecary vibes.",
    swatch: ["#0a1929", "#B87333", "#93C5FD"],
    tokens: {
      "--bg": "#0a1929", "--surface": "#0f2338", "--surface-2": "#152b44",
      "--border": "#1e3a5f", "--muted": "#64748b",
      "--text": "#f0f9ff", "--text-muted": "#94a3b8",
      "--brand": "#B87333", "--brand-fg": "#0a1929",
      "--accent": "#93C5FD", "--success": "#10B981", "--danger": "#F87171",
      "--font-display": '"Playfair Display", Georgia, serif',
      "--font-body": '"Lora", Georgia, serif',
      "--font-mono": '"IBM Plex Mono", monospace',
      "--font-scale": "1",
      "--radius-sm": "3px", "--radius-md": "6px", "--radius-lg": "10px",
      "--density-y": "1", "--density-x": "1",
    },
  },
];

export const FONT_OPTIONS = {
  display: [
    { value: '"Barlow Condensed", Impact, sans-serif', label: "Barlow Condensed", google: "Barlow+Condensed:wght@700;900" },
    { value: '"Bebas Neue", Impact, sans-serif', label: "Bebas Neue", google: "Bebas+Neue" },
    { value: '"Space Grotesk", sans-serif', label: "Space Grotesk", google: "Space+Grotesk:wght@600;700" },
    { value: '"Orbitron", sans-serif', label: "Orbitron", google: "Orbitron:wght@700;900" },
    { value: '"Playfair Display", Georgia, serif', label: "Playfair Display", google: "Playfair+Display:wght@700;900" },
    { value: '"Anton", Impact, sans-serif', label: "Anton", google: "Anton" },
    { value: '"Inter", system-ui, sans-serif', label: "Inter (bold)", google: "Inter:wght@800;900" },
  ],
  body: [
    { value: '"Manrope", Inter, system-ui, sans-serif', label: "Manrope", google: "Manrope:wght@400;500;600" },
    { value: '"Inter", system-ui, sans-serif', label: "Inter", google: "Inter:wght@400;500;600" },
    { value: '"IBM Plex Sans", system-ui, sans-serif', label: "IBM Plex Sans", google: "IBM+Plex+Sans:wght@400;500;600" },
    { value: '"Lora", Georgia, serif', label: "Lora", google: "Lora:wght@400;500;600" },
    { value: '"Rajdhani", system-ui, sans-serif', label: "Rajdhani", google: "Rajdhani:wght@400;500;600" },
    { value: 'system-ui, sans-serif', label: "System Sans", google: null },
  ],
  mono: [
    { value: '"JetBrains Mono", monospace', label: "JetBrains Mono", google: "JetBrains+Mono:wght@400;600" },
    { value: '"IBM Plex Mono", monospace', label: "IBM Plex Mono", google: "IBM+Plex+Mono:wght@400;500" },
    { value: '"Share Tech Mono", monospace', label: "Share Tech Mono", google: "Share+Tech+Mono" },
    { value: '"Fira Code", monospace', label: "Fira Code", google: "Fira+Code:wght@400;500" },
    { value: 'ui-monospace, monospace', label: "System Mono", google: null },
  ],
};

export const DENSITY_PRESETS = [
  { id: "compact", label: "Compact", tokens: { "--density-y": "0.85", "--density-x": "0.9", "--font-scale": "0.95" } },
  { id: "normal", label: "Normal", tokens: { "--density-y": "1", "--density-x": "1", "--font-scale": "1" } },
  { id: "roomy", label: "Roomy", tokens: { "--density-y": "1.2", "--density-x": "1.15", "--font-scale": "1.05" } },
];

export const DEFAULT_THEME = THEME_PRESETS[0].tokens;

/** Merge a partial theme onto the default so unset tokens still render. */
export function resolveTheme(theme) {
  return { ...DEFAULT_THEME, ...(theme || {}) };
}

/** Apply theme tokens to a document root and load required Google Fonts. */
export function applyThemeTo(root, theme) {
  const resolved = resolveTheme(theme);
  Object.entries(resolved).forEach(([k, v]) => root.style.setProperty(k, v));
  ensureFontsLoaded(resolved);
}

const LOADED_FONTS = new Set();
function ensureFontsLoaded(resolved) {
  const families = [
    resolved["--font-display"],
    resolved["--font-body"],
    resolved["--font-mono"],
  ].filter(Boolean);
  const wanted = new Set();
  families.forEach((f) => {
    const first = (f.split(",")[0] || "").replace(/["']/g, "").trim();
    const all = [...FONT_OPTIONS.display, ...FONT_OPTIONS.body, ...FONT_OPTIONS.mono];
    const opt = all.find((o) => o.label === first || o.value === f);
    if (opt?.google) wanted.add(opt.google);
  });
  wanted.forEach((g) => {
    if (LOADED_FONTS.has(g)) return;
    LOADED_FONTS.add(g);
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = `https://fonts.googleapis.com/css2?family=${g}&display=swap`;
    document.head.appendChild(l);
  });
}
